/**
 * RichTextEditorControl
 * WYSIWYG rich text editor using react-quill.
 * Falls back to a styled textarea if react-quill is unavailable.
 */

import React, { useCallback } from 'react';
import type { FormControl } from '@atlas-forms/types-js';

export interface RichTextEditorControlProps {
  control: FormControl;
  value?: unknown;
  onChange?: (value: any) => void;
  onBlur?: () => void;
  disabled?: boolean;
  mode?: string;
}

// Lazy-load react-quill to avoid hard dependency failure
let ReactQuill: React.ComponentType<any> | null = null;
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  ReactQuill = require('react-quill').default ?? require('react-quill');
} catch {
  ReactQuill = null;
}

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'code-block'],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'blockquote', 'code-block', 'link',
];

export function RichTextEditorControl({
  control,
  value,
  onChange,
  onBlur,
  disabled,
  mode,
}: RichTextEditorControlProps) {
  const strValue = typeof value === 'string' ? value : (value ? String(value) : '');
  const isReadOnly = mode === 'view' || control.readonly;
  const isDisabled = disabled || control.disabled;

  const handleChange = useCallback((html: string) => {
    onChange?.(html);
  }, [onChange]);

  if (ReactQuill) {
    return (
      <div className="rich-text-editor-control" data-control-id={control.id}>
        <ReactQuill
          theme="snow"
          value={strValue}
          onChange={handleChange}
          onBlur={onBlur}
          readOnly={isReadOnly || isDisabled}
          modules={QUILL_MODULES}
          formats={QUILL_FORMATS}
          placeholder={control.placeholder ?? 'Enter content...'}
          style={{ background: '#fff' }}
        />
      </div>
    );
  }

  // Fallback textarea
  return (
    <div className="rich-text-editor-control rich-text-editor-control--fallback" data-control-id={control.id}>
      <textarea
        id={control.id}
        value={strValue}
        placeholder={control.placeholder ?? 'Enter rich text content...'}
        disabled={isDisabled}
        readOnly={isReadOnly}
        onChange={(e) => onChange?.(e.target.value)}
        onBlur={onBlur}
        style={{
          width: '100%', minHeight: '200px', resize: 'vertical',
          padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db',
          fontFamily: 'inherit', fontSize: '14px', lineHeight: '1.5',
          boxSizing: 'border-box',
        }}
      />
    </div>
  );
}
